import {ConfigObjectType, useApiMutation, useApiQuery} from '@wallaby-ui/core';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {
  AlertEpisodeDispositionPostDto,
  AlertEpisodeDispositionResponseDto,
  AlertEpisodePanelDto,
  AlertSnoozeOptionDto,
} from './alert_types';

export type UseAlertEpisodePanelReturn = {
  panel: AlertEpisodePanelDto | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** Episodes displaying as Active, for the tab badge and the panel header; 0 while nothing is loaded. */
  activeCount: number;
};

/**
 * The Alerts panel read (PDW-182): `GET {hospitalizationDetail}/{id}/alert-episodes`, all of the patient's
 * episodes grouped and sorted by the BE. Called on page load regardless of the open tab, because the tab
 * badge needs the count. Disabled entirely for a user without the Use alerts right - the BE would answer
 * 403 and the FE hides every alert surface anyway.
 */
export function useAlertEpisodePanel(config: ConfigObjectType, hospitalizationId: string, enabled: boolean): UseAlertEpisodePanelReturn {
  const url = `${config.service.url.hospitalizationDetail}/${hospitalizationId}/alert-episodes`;
  const {data, isLoading, isError, refetch} = useApiQuery<AlertEpisodePanelDto>([url], null, {
    axiosConfig: {url},
    reactQueryOptions: {
      enabled: enabled && Boolean(hospitalizationId),
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });
  return {
    panel: enabled ? data : undefined,
    isLoading: enabled && isLoading,
    isError: enabled && isError,
    refetch: useCallback(() => {
      if (enabled) refetch();
    }, [enabled, refetch]),
    activeCount: enabled ? data?.activeCount ?? 0 : 0,
  };
}

/** The five snooze durations with their dates, fetched only when a panel response did not carry them. */
export function useAlertSnoozeOptions(config: ConfigObjectType, enabled: boolean): AlertSnoozeOptionDto[] {
  const url: string = config.service.url.alertSnoozeOptions ?? '';
  const {data} = useApiQuery<AlertSnoozeOptionDto[]>([url], null, {
    axiosConfig: {url},
    reactQueryOptions: {enabled: enabled && Boolean(url), staleTime: 60_000},
  });
  return data ?? [];
}

type PendingAction = {episodeId: number; post: AlertEpisodeDispositionPostDto; seq: number};

export type UseAlertEpisodeDispositionReturn = {
  /** Posts the action; the surface is refetched on success and on a 404 (the episode moved or is gone). */
  act: (episodeId: number, post: AlertEpisodeDispositionPostDto) => void;
  isPending: boolean;
};

/**
 * The one write a user has against an episode: `POST /api/alert-episodes/{id}/disposition`. Last write
 * wins and the BE refuses nothing by state, so there is no conflict flow; a 200 carries the new state and a
 * 404 means the alert is no longer available. Both end in a refetch of the whole surface (PDW-179 step 4),
 * because an action moves the row between groups and changes the counts.
 */
export function useAlertEpisodeDisposition(
  config: ConfigObjectType,
  onSettled: (response: AlertEpisodeDispositionResponseDto | undefined, episodeId: number) => void
): UseAlertEpisodeDispositionReturn {
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [sent, setSent] = useState(0);
  const template: string = config.service.url.alertEpisodeDisposition ?? '';
  const url = pending ? template.replace('{episodeId}', String(pending.episodeId)) : template;
  const data = useMemo(() => pending?.post ?? {disposition: 'NONE' as const}, [pending]);
  const {mutate} = useApiMutation<AlertEpisodeDispositionPostDto>(
    [url, data],
    data,
    {
      reactMutationOptions: {
        onSuccess: (response: any) => {
          const episodeId = pending?.episodeId ?? -1;
          setPending(null);
          onSettled(response as AlertEpisodeDispositionResponseDto, episodeId);
        },
        onError: () => {
          const episodeId = pending?.episodeId ?? -1;
          setPending(null);
          // a 404 (or any failure) is "the alert is no longer available": refresh the surface
          onSettled(undefined, episodeId);
        },
      },
      axiosConfig: {method: 'POST', url, data},
    },
    true
  );
  // the mutation's URL and body come from state, so the post goes out one render after the click
  useEffect(() => {
    if (pending && pending.seq !== sent) {
      setSent(pending.seq);
      mutate();
    }
  }, [pending, sent, mutate]);
  const act = useCallback((episodeId: number, post: AlertEpisodeDispositionPostDto) => {
    setPending((prev) => prev || {episodeId, post, seq: Date.now()});
  }, []);
  return {act, isPending: pending !== null};
}
